import { z } from "zod";
import { suggestionDiagnosticsSchema, suggestionSchema } from "./sessions";

/**
 * THE ENGINE BENCH, ON THE WIRE (BUD-312) — A DEVELOPMENT-ONLY SURFACE.
 *
 * ═══ WHAT IT IS FOR ═══
 *
 * The v1 prototype page ("Session Structure Prototype") is what the engine was designed
 * against: pick an athlete, pick a room, RE-ROLL, and read one session across three gear
 * columns with every block shown. This contract is the v2 half of that comparison — the whole
 * offer, so the two pages can be read side by side.
 *
 * 🕰️ HISTORICAL (BUD-364): that v1 page is a design sketch, not a specification. It carried 48
 * tuning dials against the engine's own (157 and counting — `api/src/engine/parameters/index.ts`)
 * and a fixed `sessionMinMin` of 40 where the engine's floor is now 16, with no knowledge of
 * pairing labels, giant sets, the substitution bands, the heavy-day cap, the browse list or the
 * athlete's wishes. See `api/scripts/asset-overview.mjs` (`make asset-overview`) for the
 * generated, always-current inventory of what the engine actually has.
 *
 * ═══ IT IS NOT MOUNTED OUTSIDE DEVELOPMENT, AND BOTH HALVES ARE ASSERTED ═══
 *
 *   · `api/src/app.ts` mounts `/v1/bench` only when `APP_ENV` is `dev`. Proven by
 *     `api/tests/bench.test.ts`, which boots the service as production and asks for a 404.
 *   · `vite.config.ts` hides `src/routes/bench.tsx` from the route generator in every mode
 *     but `dev`. Proven by `scripts/check/check-dev-only.mjs`, which greps the built
 *     production bundle.
 *
 * This file itself ships everywhere — it is a schema, it holds no behaviour, and `shared/` is
 * compiled by both halves of the repo. What must not ship is the ENDPOINT and the ROUTE.
 *
 * ═══ WHY IT DOES NOT REUSE THE DECK'S ENDPOINT ═══
 *
 * `/v1/sessions/suggestions` deliberately drops `diagnostics` (BUD-305: a ten-card deck was
 * 176 kB and the draw's own record is not what that screen reads). The bench wants exactly
 * that field — what was considered and turned down is half of what a comparison is for — so it
 * is its own action. What it does NOT do is declare a second session shape: everything below
 * composes `sessions.ts`, so a block, a line, a load and a reason mean one thing on this wire.
 */

/* ══════════════════════════════════════════════════════════════════════════════════════
   THE TWENTY COMBINATIONS

   FOUR ATHLETES × FIVE ROOMS, and these are not the bench's own idea of a preset: they are
   the ids in the engine's own preset fixtures (`api/src/engine/library/fixtures/`), which are
   the same twenty the golden fixtures under `api/tests/engine/fixtures/` are frozen from.
   `api/tests/engine/engine-bench.int.spec.ts` asserts both lists against the engine's, so a
   preset renamed there cannot leave a dead button here.

   THEY ARE ENUMS RATHER THAN OPEN STRINGS because this is the one contract in the repo whose
   input space is genuinely closed: a preset is a fixture, not content a coach may extend.
   ══════════════════════════════════════════════════════════════════════════════════════ */

export const BENCH_ATHLETES = ["beginner", "inter", "advanced", "returning"] as const;
export const benchAthleteSchema = z.enum(BENCH_ATHLETES);
export type BenchAthlete = (typeof BENCH_ATHLETES)[number];

export const BENCH_ROOMS = ["box", "garage", "home", "hotel", "bw"] as const;
export const benchRoomSchema = z.enum(BENCH_ROOMS);
export type BenchRoom = (typeof BENCH_ROOMS)[number];

/**
 * WHAT THE BENCH ASKS FOR.
 *
 * `z.coerce` on the numbers because this validates a QUERY STRING — `?seed=1040711` arrives as
 * "1040711" — the same reason `suggestionsQuerySchema` coerces its cursor.
 *
 * `minutes` IS ON THE REQUEST AND HAS A DEFAULT. `SessionRequest.minutesAvailable` is required
 * by the engine and there is no athlete here to read it from, so the bench states it. 55 is the
 * number `api/tests/engine/engine-contract.int.spec.ts` drives the same twenty combinations at,
 * which is what makes a bench reading comparable to that spec's.
 */
export const benchOfferQuerySchema = z.object({
  athlete: benchAthleteSchema,
  room: benchRoomSchema,
  seed: z.coerce.number().int().min(0),
  minutes: z.coerce.number().int().min(10).max(180).default(55),
});

export type BenchOfferQuery = z.infer<typeof benchOfferQuerySchema>;

/**
 * THE WHOLE OFFER — the deck's suggestion with `diagnostics` made REQUIRED rather than
 * optional. That single difference is the entire reason this action exists rather than the
 * deck's; see the header.
 */
export const benchOfferSchema = suggestionSchema.extend({
  diagnostics: suggestionDiagnosticsSchema,
});

export type BenchOfferDTO = z.infer<typeof benchOfferSchema>;

/** One preset button: the id the engine knows it by, and the label a person reads. */
export const benchPresetSchema = z.object({
  id: z.string(),
  name: z.string(),
});

export type BenchPresetDTO = z.infer<typeof benchPresetSchema>;

/**
 * ONE FROZEN CASE: an athlete, a room, and THE SEED THE GOLDEN FIXTURE FOR THAT PAIR WAS
 * WRITTEN AT.
 *
 * The bench opens on these rather than on a seed of its own, because a seed that matches a
 * fixture is the one reading whose correctness can be checked against something frozen. Every
 * other seed is a re-roll, and a re-roll is only ever "plausible".
 */
export const benchGoldenSeedSchema = z.object({
  athlete: benchAthleteSchema,
  room: benchRoomSchema,
  seed: z.number().int().min(0),
});

export type BenchGoldenSeedDTO = z.infer<typeof benchGoldenSeedSchema>;

export const benchPresetsSchema = z.object({
  athletes: z.array(benchPresetSchema),
  rooms: z.array(benchPresetSchema),
  /** The twenty golden seeds, so the page can open on a reading that is checkable. */
  goldenSeeds: z.array(benchGoldenSeedSchema),
});

export type BenchPresetsDTO = z.infer<typeof benchPresetsSchema>;
